import React, { useState, useMemo, useCallback } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";

import {
  Calculator,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  TrendingUp,
  Hash,
  Sigma,
  Info,
  ChevronRight
} from 'lucide-react';

// Arithmetic Sequence Functions
const arithmeticNthTerm = (a1, d, n) => a1 + (n - 1) * d;
const arithmeticSum = (a1, d, n) => (n / 2) * (2 * a1 + (n - 1) * d);
const generateArithmeticSequence = (a1, d, n) => {
  const terms = [];
  for (let i = 1; i <= n; i++) {
    terms.push({ n: i, term: arithmeticNthTerm(a1, d, i) });
  }
  return terms;
};

// Geometric Sequence Functions
const geometricNthTerm = (a1, r, n) => a1 * Math.pow(r, n - 1);
const geometricSum = (a1, r, n) => {
  if (r === 1) return a1 * n;
  return a1 * (1 - Math.pow(r, n)) / (1 - r);
};
const geometricInfiniteSum = (a1, r) => {
  if (Math.abs(r) >= 1) return null; // Diverges
  return a1 / (1 - r);
};
const generateGeometricSequence = (a1, r, n) => {
  const terms = [];
  for (let i = 1; i <= n; i++) {
    terms.push({ n: i, term: geometricNthTerm(a1, r, i) });
  }
  return terms;
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(String(text));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Sequence Visualization Component
const SequenceVisualization = ({ data, type }) => {
  const maxTerms = 15;
  const displayData = data.slice(0, maxTerms);
  const chartColor = type === 'arithmetic' ? '#0ea5e9' : '#8b5cf6';
  
  return (
    <div className="space-y-4" data-testid="sequence-visualization">
      <div className="h-[200px]" data-testid="sequence-graph">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={displayData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="n" 
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'n', position: 'bottom', fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'aₙ', angle: -90, position: 'left', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value) => [value.toFixed(4), 'Term']}
              labelFormatter={(label) => `n = ${label}`}
            />
            <Line
              type="monotone"
              dataKey="term"
              stroke={chartColor}
              strokeWidth={2}
              dot={{ fill: chartColor, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Term Display */}
      <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg">
        {displayData.map((item, i) => (
          <div 
            key={i} 
            className={`px-2 py-1 rounded text-xs font-mono ${type === 'arithmetic' ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400' : 'bg-violet-500/10 text-violet-600 dark:text-violet-400'}`}
          >
            a<sub>{item.n}</sub> = {item.term.toFixed(2)}
          </div>
        ))}
        {data.length > maxTerms && (
          <div className="px-2 py-1 rounded text-xs font-mono text-muted-foreground">...</div>
        )}
      </div>
    </div>
  );
};

// Sum Visualization Component
const SumVisualization = ({ data, sum, type }) => {
  const maxTerms = 15;
  const displayData = data.slice(0, maxTerms);
  const chartColor = type === 'arithmetic' ? '#0ea5e9' : '#8b5cf6';
  
  // Calculate partial sums
  let partialSum = 0;
  const sumData = displayData.map(item => {
    partialSum += item.term;
    return { n: item.n, term: item.term, partialSum };
  });
  
  return (
    <div className="space-y-4">
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sumData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="n" 
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value, name) => [
                value.toFixed(4), 
                name === 'term' ? 'Term' : 'Running Sum'
              ]}
            />
            <Bar dataKey="term" fill={chartColor} opacity={0.7} />
            <Line
              type="monotone"
              dataKey="partialSum"
              stroke="#f97316"
              strokeWidth={2}
              dot={{ fill: '#f97316', r: 3 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <Sigma className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="font-medium text-foreground">Total Sum</span>
        </div>
        <div className="font-mono text-2xl font-bold text-amber-600 dark:text-amber-400">
          {sum.toFixed(4)}
        </div>
      </div>
    </div>
  );
};

function SequencesCalculator() {
  const [seqType, setSeqType] = useState('arithmetic');
  const [calcMode, setCalcMode] = useState('sequence');
  const [firstTerm, setFirstTerm] = useState('2');
  const [commonDiff, setCommonDiff] = useState('3');
  const [commonRatio, setCommonRatio] = useState('2');
  const [nTerms, setNTerms] = useState('10');
  const [findNth, setFindNth] = useState('5');
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const a1 = parseFloat(firstTerm) || 0;
  const d = parseFloat(commonDiff) || 0;
  const r = parseFloat(commonRatio) || 1;
  const n = parseInt(nTerms) || 1;
  const nth = parseInt(findNth) || 1;

  // Calculations
  const sequenceData = useMemo(() => {
    if (seqType === 'arithmetic') {
      return generateArithmeticSequence(a1, d, Math.min(n, 50));
    } else {
      return generateGeometricSequence(a1, r, Math.min(n, 50));
    }
  }, [seqType, a1, d, r, n]);

  const results = useMemo(() => {
    if (seqType === 'arithmetic') {
      return {
        nthTerm: arithmeticNthTerm(a1, d, nth),
        sum: arithmeticSum(a1, d, n),
        formula: `aₙ = ${a1} + (n-1)×${d}`,
        sumFormula: `Sₙ = (n/2)[2a₁ + (n-1)d]`,
        lastTerm: arithmeticNthTerm(a1, d, n)
      };
    } else {
      return {
        nthTerm: geometricNthTerm(a1, r, nth),
        sum: geometricSum(a1, r, n),
        infiniteSum: geometricInfiniteSum(a1, r),
        formula: `aₙ = ${a1} × ${r}^(n-1)`,
        sumFormula: `Sₙ = a₁(1-rⁿ)/(1-r)`,
        lastTerm: geometricNthTerm(a1, r, n),
        converges: Math.abs(r) < 1
      };
    }
  }, [seqType, a1, d, r, n, nth]);

  const handleReset = useCallback(() => {
    setFirstTerm('2');
    setCommonDiff('3');
    setCommonRatio('2');
    setNTerms('10');
    setFindNth('5');
  }, []);

  const quickExamples = seqType === 'arithmetic' 
    ? [
        { a1: '1', d: '1', n: '100', label: '1+2+3+...+100' },
        { a1: '2', d: '2', n: '50', label: 'Even numbers' },
        { a1: '5', d: '-2', n: '10', label: 'Decreasing' },
      ]
    : [
        { a1: '1', r: '2', n: '10', label: 'Powers of 2' },
        { a1: '100', r: '0.5', n: '8', label: 'Halving' },
        { a1: '3', r: '3', n: '5', label: 'Powers of 3' },
      ];

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Sequences & Series Calculator - Arithmetic & Geometric</title>
          <meta name="description" content="Calculate arithmetic and geometric sequences and series. Find nth terms, sums, and visualize patterns with step-by-step explanations." />
        </Helmet>
        
        <div className="container mx-auto px-4 py-8" data-testid="sequences-calculator">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500/20 to-violet-500/20 flex items-center justify-center">
                <Hash className="w-6 h-6 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Sequences & Series</h1>
                <p className="text-muted-foreground">Arithmetic & Geometric calculations with visualization</p>
              </div>
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Calculator Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start"
            >
              <Card className="rounded-2xl p-6 border bg-card">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                    <span className="font-semibold text-foreground">Calculator</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={handleReset}
                    title="Reset"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>

                {/* Sequence Type Toggle */}
                <Tabs value={seqType} onValueChange={setSeqType} className="mb-4">
                  <TabsList className="w-full">
                    <TabsTrigger value="arithmetic" className="flex-1" data-testid="arithmetic-tab">
                      Arithmetic
                    </TabsTrigger>
                    <TabsTrigger value="geometric" className="flex-1" data-testid="geometric-tab">
                      Geometric
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Mode Toggle */}
                <Tabs value={calcMode} onValueChange={setCalcMode} className="mb-6">
                  <TabsList className="w-full">
                    <TabsTrigger value="sequence" className="flex-1">
                      <TrendingUp className="w-4 h-4 mr-2" />Sequence
                    </TabsTrigger>
                    <TabsTrigger value="series" className="flex-1">
                      <Sigma className="w-4 h-4 mr-2" />Series
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="space-y-4">
                  {/* First Term */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">First Term (a₁)</Label>
                    <Input
                      type="number"
                      value={firstTerm}
                      onChange={(e) => setFirstTerm(e.target.value)}
                      className="font-mono"
                      placeholder="2"
                      data-testid="first-term-input"
                    />
                  </div>
                  
                  {/* Common Difference or Ratio */}
                  {seqType === 'arithmetic' ? (
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Common Difference (d)</Label>
                      <Input
                        type="number"
                        value={commonDiff}
                        onChange={(e) => setCommonDiff(e.target.value)}
                        className="font-mono"
                        placeholder="3"
                        data-testid="common-diff-input"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Common Ratio (r)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={commonRatio}
                        onChange={(e) => setCommonRatio(e.target.value)}
                        className="font-mono"
                        placeholder="2"
                        data-testid="common-ratio-input"
                      />
                    </div>
                  )}
                  
                  {/* Number of Terms */}
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Number of Terms (n)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={nTerms}
                      onChange={(e) => setNTerms(e.target.value)}
                      className="font-mono"
                      placeholder="10"
                      data-testid="n-terms-input"
                    />
                  </div>
                  
                  {/* Find nth term */}
                  {calcMode === 'sequence' && (
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Find Term Number</Label>
                      <Input
                        type="number"
                        min="1"
                        value={findNth}
                        onChange={(e) => setFindNth(e.target.value)}
                        className="font-mono"
                        placeholder="5"
                        data-testid="find-nth-input"
                      />
                    </div>
                  )}

                  {/* Quick Examples */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                    <div className="flex flex-wrap gap-2">
                      {quickExamples.map((ex, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setFirstTerm(ex.a1);
                            if (seqType === 'arithmetic') setCommonDiff(ex.d);
                            else setCommonRatio(ex.r);
                            setNTerms(ex.n);
                          }}
                        >
                          {ex.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Results */}
                  <div className="pt-4 border-t border-border space-y-3">
                    <Label className="text-sm font-medium text-foreground">Results</Label>
                    
                    {calcMode === 'sequence' ? (
                      <>
                        <div className={`rounded-xl p-4 ${seqType === 'arithmetic' ? 'bg-sky-500/10 border border-sky-500/20' : 'bg-violet-500/10 border border-violet-500/20'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">a<sub>{nth}</sub> (Term {nth})</span>
                            <CopyButton text={results.nthTerm} />
                          </div>
                          <span className={`font-mono text-lg font-semibold block ${seqType === 'arithmetic' ? 'text-sky-600 dark:text-sky-400' : 'text-violet-600 dark:text-violet-400'}`}>
                            {isFinite(results.nthTerm) ? results.nthTerm.toFixed(4) : 'Overflow'}
                          </span>
                        </div>
                        
                        <div className="rounded-xl p-3 bg-muted/50">
                          <span className="text-xs text-muted-foreground block mb-1">Formula</span>
                          <span className="font-mono text-sm text-foreground">{results.formula}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-xl p-4 bg-amber-500/10 border border-amber-500/20">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">S<sub>{n}</sub> (Sum of {n} terms)</span>
                            <CopyButton text={results.sum} />
                          </div>
                          <span className="font-mono text-lg font-semibold text-amber-600 dark:text-amber-400 block">
                            {isFinite(results.sum) ? results.sum.toFixed(4) : 'Overflow'}
                          </span>
                        </div>
                        
                        {seqType === 'geometric' && (
                          <div className={`rounded-xl p-3 ${results.converges ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-rose-500/10 border border-rose-500/20'}`}>
                            <span className="text-xs text-muted-foreground block mb-1">S<sub>∞</sub> (Infinite Sum)</span>
                            <span className={`font-mono text-sm font-semibold ${results.converges ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
                              {results.converges ? results.infiniteSum?.toFixed(4) : 'Diverges (|r| ≥ 1)'}
                            </span>
                          </div>
                        )}
                        
                        <div className="rounded-xl p-3 bg-muted/50">
                          <span className="text-xs text-muted-foreground block mb-1">Sum Formula</span>
                          <span className="font-mono text-sm text-foreground">{results.sumFormula}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Center/Right Column - Visualization & Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Visualization Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="rounded-2xl p-6 border bg-card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-foreground flex items-center gap-2">
                      <Info className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                      {calcMode === 'sequence' ? 'Sequence' : 'Series'} Visualization
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {seqType === 'arithmetic' ? 'Arithmetic' : 'Geometric'}
                      </Badge>
                    </h2>
                  </div>
                  
                  {calcMode === 'sequence' ? (
                    <SequenceVisualization data={sequenceData} type={seqType} />
                  ) : (
                    <SumVisualization data={sequenceData} sum={results.sum} type={seqType} />
                  )}
                  
                  {/* Pattern Recognition */}
                  <div className="mt-4 p-4 bg-muted/30 rounded-xl">
                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      Pattern Recognition
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {seqType === 'arithmetic' 
                        ? `Each term increases by ${d}. The sequence ${d >= 0 ? 'grows' : 'decreases'} linearly.`
                        : `Each term is multiplied by ${r}. The sequence ${r > 1 ? 'grows exponentially' : r > 0 && r < 1 ? 'decreases exponentially toward 0' : r < 0 ? 'alternates in sign' : 'stays constant'}.`
                      }
                    </p>
                    {seqType === 'geometric' && Math.abs(r) < 1 && calcMode === 'series' && (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                        Since |r| &lt; 1, this infinite series converges to {results.infiniteSum?.toFixed(4)}
                      </p>
                    )}
                  </div>
                </Card>
              </motion.div>

              {/* Educational Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Card className="rounded-2xl p-6 border bg-card">
                  <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                    Understanding Sequences & Series
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Arithmetic */}
                    <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
                      <h3 className="font-bold text-lg text-sky-600 dark:text-sky-400 mb-2 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" /> Arithmetic Sequence
                      </h3>
                      <p className="text-muted-foreground mb-3">
                        <strong>Simple Definition:</strong> A sequence where you <em>add the same number</em> each time.
                      </p>
                      <div className="bg-background rounded-lg p-3 mb-3">
                        <p className="text-sm">Example: 2, 5, 8, 11, 14... (adding 3 each time)</p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>• <strong>nth term:</strong> aₙ = a₁ + (n-1)d</li>
                          <li>• <strong>Sum of n terms:</strong> Sₙ = (n/2)[2a₁ + (n-1)d] = (n/2)(first + last)</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <strong>Real example:</strong> Saving $100 first month, then $50 more each month
                      </p>
                    </div>
                    
                    {/* Geometric */}
                    <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                      <h3 className="font-bold text-lg text-violet-600 dark:text-violet-400 mb-2 flex items-center gap-2">
                        <ChevronRight className="w-5 h-5" /> Geometric Sequence
                      </h3>
                      <p className="text-muted-foreground mb-3">
                        <strong>Simple Definition:</strong> A sequence where you <em>multiply by the same number</em> each time.
                      </p>
                      <div className="bg-background rounded-lg p-3 mb-3">
                        <p className="text-sm">Example: 2, 6, 18, 54... (multiplying by 3 each time)</p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>• <strong>nth term:</strong> aₙ = a₁ × r^(n-1)</li>
                          <li>• <strong>Sum of n terms:</strong> Sₙ = a₁(1-rⁿ)/(1-r)</li>
                          <li>• <strong>Infinite sum (|r| &lt; 1):</strong> S∞ = a₁/(1-r)</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <strong>Real example:</strong> Compound interest, bacterial growth, radioactive decay
                      </p>
                    </div>
                    
                    {/* Quick Comparison */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Property</th>
                            <th className="text-center py-2 text-sky-500">Arithmetic</th>
                            <th className="text-center py-2 text-violet-500">Geometric</th>
                          </tr>
                        </thead>
                        <tbody className="text-muted-foreground">
                          <tr className="border-b">
                            <td className="py-2">Pattern</td>
                            <td className="text-center">Add d each time</td>
                            <td className="text-center">Multiply by r</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2">nth Term</td>
                            <td className="text-center font-mono">a₁ + (n-1)d</td>
                            <td className="text-center font-mono">a₁ × r^(n-1)</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2">Growth</td>
                            <td className="text-center">Linear</td>
                            <td className="text-center">Exponential</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2">Graph Shape</td>
                            <td className="text-center">Straight line</td>
                            <td className="text-center">Curve</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Step-by-Step Solution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="rounded-2xl p-6 border bg-card">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                    <BookOpen className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                    Step-by-Step Solution
                  </h2>

                  <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the sequence type</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <p className="text-muted-foreground">
                            This is a <span className="font-semibold text-foreground">{seqType}</span> sequence.
                          </p>
                          <div className="bg-secondary/50 rounded-lg p-3">
                            <p className="text-sm">
                              First term (a₁) = <span className="font-mono font-semibold">{a1}</span><br />
                              {seqType === 'arithmetic' 
                                ? <>Common difference (d) = <span className="font-mono font-semibold">{d}</span></>
                                : <>Common ratio (r) = <span className="font-mono font-semibold">{r}</span></>}
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Apply the formula</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          <div className="bg-secondary/50 rounded-lg p-4">
                            {calcMode === 'sequence' ? (
                              <>
                                <p className="text-sm font-medium mb-2">Finding a{nth}:</p>
                                {seqType === 'arithmetic' ? (
                                  <div className="font-mono text-sm space-y-1">
                                    <div>aₙ = a₁ + (n-1)d</div>
                                    <div>a{nth} = {a1} + ({nth}-1) × {d}</div>
                                    <div>a{nth} = {a1} + {nth - 1} × {d}</div>
                                    <div>a{nth} = {a1} + {(nth - 1) * d}</div>
                                    <div className="text-sky-600 dark:text-sky-400 font-bold">a{nth} = {results.nthTerm?.toFixed(4)}</div>
                                  </div>
                                ) : (
                                  <div className="font-mono text-sm space-y-1">
                                    <div>aₙ = a₁ × r^(n-1)</div>
                                    <div>a{nth} = {a1} × {r}^({nth}-1)</div>
                                    <div>a{nth} = {a1} × {r}^{nth - 1}</div>
                                    <div className="text-violet-600 dark:text-violet-400 font-bold">a{nth} = {results.nthTerm?.toFixed(4)}</div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium mb-2">Finding S{n}:</p>
                                {seqType === 'arithmetic' ? (
                                  <div className="font-mono text-sm space-y-1">
                                    <div>Sₙ = (n/2)[2a₁ + (n-1)d]</div>
                                    <div>S{n} = ({n}/2)[2×{a1} + ({n}-1)×{d}]</div>
                                    <div>S{n} = {n / 2} × [{2 * a1} + {(n - 1) * d}]</div>
                                    <div>S{n} = {n / 2} × {2 * a1 + (n - 1) * d}</div>
                                    <div className="text-amber-600 dark:text-amber-400 font-bold">S{n} = {results.sum?.toFixed(4)}</div>
                                  </div>
                                ) : (
                                  <div className="font-mono text-sm space-y-1">
                                    <div>Sₙ = a₁(1-rⁿ)/(1-r)</div>
                                    <div>S{n} = {a1}(1-{r}^{n})/(1-{r})</div>
                                    <div>S{n} = {a1} × (1-{Math.pow(r, n).toFixed(4)}) / {1 - r}</div>
                                    <div className="text-amber-600 dark:text-amber-400 font-bold">S{n} = {results.sum?.toFixed(4)}</div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Verify the result</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11">
                          <div className="bg-emerald-500/10 rounded-lg p-3">
                            <p className="text-sm text-muted-foreground mb-2">Verification:</p>
                            <p className="text-sm">
                              The first few terms are: {sequenceData.slice(0, 5).map(t => t.term.toFixed(2)).join(', ')}...
                            </p>
                            {calcMode === 'series' && (
                              <p className="text-sm mt-2">
                                Adding these: {sequenceData.slice(0, 5).reduce((a, b) => a + b.term, 0).toFixed(2)}... → Total S{n} = {results.sum?.toFixed(4)}
                              </p>
                            )}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </motion.div>

              {/* Common Mistakes */}
              <Card className="rounded-2xl p-6 bg-rose-500/5 border border-rose-500/20">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-500" />
                  Common Mistakes to Avoid
                </h2>
                <ul className="space-y-3">
                  {[
                    'Confusing arithmetic (add) and geometric (multiply) sequences',
                    'Using n instead of (n-1) in the formulas - the first term is when n=1, not n=0',
                    'Forgetting that infinite geometric series only converge when |r| < 1',
                    'Sign errors with negative common differences or ratios',
                    'Trying to find infinite sum when r ≥ 1 or r ≤ -1 (series diverges!)',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* FAQ Section */}
              <Card className="rounded-2xl p-6 border bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    {
                      q: 'How do I know if a sequence is arithmetic or geometric?',
                      a: 'Check the pattern: if consecutive terms have a constant DIFFERENCE, it\'s arithmetic. If they have a constant RATIO (divide consecutive terms), it\'s geometric.'
                    },
                    {
                      q: 'When does an infinite geometric series converge?',
                      a: 'Only when |r| < 1 (the absolute value of the common ratio is less than 1). This means the terms get smaller and smaller, approaching zero.'
                    },
                    {
                      q: 'What is the sum of 1 + 2 + 3 + ... + 100?',
                      a: 'This is an arithmetic series with a₁=1, d=1, n=100. Using the formula: S = (100/2)(1+100) = 50 × 101 = 5050. Young Gauss famously solved this as a child!'
                    },
                    {
                      q: 'Can a sequence be both arithmetic and geometric?',
                      a: 'Only constant sequences (like 5, 5, 5, 5...) are both - they have d=0 and r=1. All other sequences are one or the other (or neither).'
                    },
                  ].map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>

              {/* Real World Applications */}
              <Card className="rounded-2xl p-6 border bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
                <ul className="space-y-3">
                  {[
                    'Compound Interest: Your savings grow geometrically when interest compounds',
                    'Loan Payments: Fixed monthly payments form an arithmetic sequence',
                    'Population Growth: Bacteria and populations often grow geometrically',
                    'Depreciation: Assets may lose value arithmetically or geometrically',
                    'Music: Frequencies of musical notes form a geometric sequence',
                    'Architecture: Seating arrangements in amphitheaters use arithmetic sequences',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ArrowRight className="w-4 h-4 text-sky-500 mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}

export default SequencesCalculator;
